import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../api/client.js";
import { compressImageForUpload, type UploadImageMime } from "../util/imageCompression.js";

/**
 * Property File ("binder") API contract — kept in lockstep with
 * customer/api/src/domain/property-binder.ts. User-facing name is "Property
 * File"; the code keeps the `binder` codename. Media is added in Slice 3.
 */

export type BinderEntryType = "asset" | "note" | "maintenance_log" | "project_reference";

export type BinderTemplateId =
  | "blank"
  | "water_heater"
  | "air_filter"
  | "fixture"
  | "yard_note"
  | "maintenance_note";

export type FactValueType = "text" | "number" | "boolean" | "select";

export interface BinderFact {
  label: string;
  valueType: FactValueType;
  value: string | number | boolean;
  unit?: string;
}

export type DatePrecision = "year" | "month" | "day";

export interface BinderDateValue {
  value: string;
  precision: DatePrecision;
  estimated?: boolean;
}

export interface BinderMedia {
  id: string;
  key: string;
  contentType: string;
  sizeBytes: number;
  caption?: string;
  createdAt: string;
}

export interface BinderEntry {
  id: string;
  ownerSub: string;
  propertyId: string;
  entryType: BinderEntryType;
  templateId?: BinderTemplateId;
  title: string;
  locationLabel?: string;
  notes?: string;
  tags?: string[];
  facts?: Record<string, BinderFact>;
  dates?: Record<string, BinderDateValue>;
  media?: BinderMedia[];
  archivedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateBinderEntryInput {
  entryType?: BinderEntryType;
  templateId?: BinderTemplateId;
  title: string;
  locationLabel?: string;
  notes?: string;
  tags?: string[];
  facts?: Record<string, BinderFact>;
  dates?: Record<string, BinderDateValue>;
}

export type UpdateBinderEntryInput = Partial<
  Pick<CreateBinderEntryInput, "title" | "locationLabel" | "notes" | "tags" | "facts" | "dates">
>;

const QK = {
  list: (propertyId: string) => ["properties", propertyId, "binder"] as const,
  detail: (propertyId: string, entryId: string) =>
    ["properties", propertyId, "binder", entryId] as const,
};

const base = (propertyId: string) => `/properties/${propertyId}/binder`;

export function useBinderList(propertyId: string | undefined) {
  return useQuery({
    queryKey: propertyId ? QK.list(propertyId) : ["properties", "noop", "binder"],
    enabled: !!propertyId,
    queryFn: async () => {
      const res = await apiFetch<{ items: BinderEntry[] }>(base(propertyId!));
      return res.items;
    },
  });
}

export function useBinderEntry(propertyId: string | undefined, entryId: string | undefined) {
  return useQuery({
    queryKey:
      propertyId && entryId
        ? QK.detail(propertyId, entryId)
        : ["properties", "noop", "binder", "noop"],
    enabled: !!propertyId && !!entryId,
    queryFn: () => apiFetch<BinderEntry>(`${base(propertyId!)}/${entryId!}`),
  });
}

export function useCreateBinderEntry(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateBinderEntryInput) =>
      apiFetch<BinderEntry>(base(propertyId), { method: "POST", json: input }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: QK.list(propertyId) });
    },
  });
}

export function useUpdateBinderEntry(propertyId: string, entryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateBinderEntryInput) =>
      apiFetch<BinderEntry>(`${base(propertyId)}/${entryId}`, { method: "PATCH", json: input }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.detail(propertyId, entryId), updated);
      void qc.invalidateQueries({ queryKey: QK.list(propertyId) });
    },
  });
}

export function useArchiveBinderEntry(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entryId: string) =>
      apiFetch<BinderEntry>(`${base(propertyId)}/${entryId}`, { method: "DELETE" }),
    onSuccess: (_data, entryId) => {
      qc.removeQueries({ queryKey: QK.detail(propertyId, entryId) });
      void qc.invalidateQueries({ queryKey: QK.list(propertyId) });
    },
  });
}

// ---- Media ---------------------------------------------------------------

export type BinderMediaContentType = UploadImageMime;

interface PresignBinderMediaResponse {
  mediaId: string;
  key: string;
  contentType: BinderMediaContentType;
  uploadUrl: string;
  expiresInSeconds: number;
}

export interface BinderMediaUrlResponse {
  mediaId: string;
  contentType: string;
  sizeBytes: number;
  url: string;
  expiresInSeconds: number;
}

/**
 * Presign → upload-to-S3 → attach, in order against the same entry. Returns the
 * updated entry. Mirrors uploadRfqImage; nothing reaches S3 until the entry
 * exists, so callers create the entry first then upload.
 */
export async function uploadBinderMedia(args: {
  propertyId: string;
  entryId: string;
  file: File;
  contentType: BinderMediaContentType;
  onProgress?: (loaded: number, total: number) => void;
}): Promise<BinderEntry> {
  const root = `${base(args.propertyId)}/${args.entryId}/media`;
  // Compress before presign — it can change the content type, which is signed.
  const { file, contentType } = await compressImageForUpload(args.file, args.contentType);
  const presign = await apiFetch<PresignBinderMediaResponse>(`${root}/presign`, {
    method: "POST",
    json: { contentType },
  });

  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", presign.uploadUrl);
    xhr.setRequestHeader("Content-Type", presign.contentType);
    xhr.upload.onprogress = (ev) => {
      if (ev.lengthComputable) args.onProgress?.(ev.loaded, ev.total);
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`S3 upload failed: HTTP ${xhr.status}`));
    xhr.onerror = () => reject(new Error("S3 upload network error"));
    xhr.send(file);
  });

  return apiFetch<BinderEntry>(root, {
    method: "POST",
    json: {
      mediaId: presign.mediaId,
      key: presign.key,
      contentType: presign.contentType,
      sizeBytes: file.size,
    },
  });
}

export function useBinderMediaUrl(
  propertyId: string,
  entryId: string,
  mediaId: string | undefined,
) {
  return useQuery({
    queryKey: mediaId
      ? ([...QK.detail(propertyId, entryId), "media", mediaId, "url"] as const)
      : ["properties", propertyId, "binder", entryId, "media", "noop"],
    enabled: !!mediaId,
    queryFn: () =>
      apiFetch<BinderMediaUrlResponse>(`${base(propertyId)}/${entryId}/media/${mediaId!}/url`),
    // Presigned GET URLs expire in 5 minutes; refetch a little before then.
    staleTime: 4 * 60 * 1000,
    gcTime: 4 * 60 * 1000,
  });
}

export function useDeleteBinderMedia(propertyId: string, entryId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (mediaId: string) =>
      apiFetch<BinderEntry>(`${base(propertyId)}/${entryId}/media/${mediaId}`, { method: "DELETE" }),
    onSuccess: (updated) => {
      qc.setQueryData(QK.detail(propertyId, entryId), updated);
      void qc.invalidateQueries({ queryKey: QK.list(propertyId) });
    },
  });
}
